import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types'

import { strapiPageToLinkTo } from './pageLink'

const defaultLocale = 'de-DE'

type StrapiMedia = {
  name?: string | null
  alternativeText?: string | null
  caption?: string | null
  mime?: string | null
  url?: string | null
  localFile?: {
    publicURL?: string | null
    childImageSharp?: MCDC.Contentful.IAssetImage['localFile']['childImageSharp']
    svg?: {
      content?: string | null
      originalContent?: string | null
    } | null
  } | null
}

type StrapiCopyChild = {
  type?: string | null
  text?: string | null
  bold?: boolean | null
  italic?: boolean | null
  underline?: boolean | null
  url?: string | null
  children?: Array<StrapiCopyChild | null> | null
}

type StrapiCopyBlock = {
  type?: string | null
  level?: number | null
  format?: string | null
  children?: Array<StrapiCopyChild | null> | null
}

type StrapiGlobalImage = {
  id: string
  theme?: string | null
  large?: StrapiMedia | null
  small?: StrapiMedia | null
}

type StrapiModuleLink = {
  id?: string
  __typename?: string
  entry?: Record<string, unknown> | null
}

type StrapiLinkToParams = {
  filters?: string[] | null
}

function adaptStrapiPageRelation(
  page?: {
    id?: string
    label?: string | null
    slug?: string | null
    path?: string | null
    url?: string | null
    metaType?: string | null
    titleInternal?: string | null
    identifier?: string | null
    title?: string | null
  } | null
): MCDC.Contentful.UnionPageTypes | undefined {
  if (!page?.id && !page?.url && !page?.slug && !page?.identifier) {
    return undefined
  }

  const pageId = page.id || page.url || page.slug || page.identifier || ''
  const linkTo = strapiPageToLinkTo(page)
  const breadcrumbs: MCDC.Contentful.ILinkTo[] = []

  if (page.url) {
    return {
      __typename: 'ContentfulPageExternal',
      id: pageId,
      contentful_id: pageId,
      node_locale: defaultLocale,
      label: page.label || '',
      url: page.url,
      fields: { linkTo, breadcrumbs },
    } as MCDC.Contentful.IPageExternal
  }

  if (page.identifier) {
    return {
      __typename: 'ContentfulPageProfile',
      id: pageId,
      contentful_id: pageId,
      node_locale: defaultLocale,
      identifier: page.identifier,
      title: page.title || '',
      label: page.label || '',
      fields: { linkTo, breadcrumbs },
    } as MCDC.Contentful.IPageProfile
  }

  return {
    __typename: 'ContentfulPageGeneric',
    id: pageId,
    contentful_id: pageId,
    node_locale: defaultLocale,
    label: page.label || '',
    slug: page.slug || '',
    metaType: page.metaType,
    titleInternal: page.titleInternal || '',
    fields: { linkTo, breadcrumbs },
  } as MCDC.Contentful.IPageGeneric
}

function adaptStrapiPageLinkComponent(
  linkTo?:
    | {
        __typename?: string
        entry?: {
          id?: string
          label?: string | null
          slug?: string | null
          path?: string | null
          url?: string | null
          metaType?: string | null
          titleInternal?: string | null
          identifier?: string | null
          title?: string | null
        } | null
      }
    | Array<{
        __typename?: string
        entry?: {
          id?: string
          label?: string | null
          slug?: string | null
          path?: string | null
          url?: string | null
          metaType?: string | null
          titleInternal?: string | null
          identifier?: string | null
          title?: string | null
        } | null
      } | null>
    | null
): MCDC.Contentful.UnionPageTypes | undefined {
  const items = Array.isArray(linkTo) ? linkTo : linkTo ? [linkTo] : []
  const first = items.find(
    (item) =>
      item?.entry?.id ||
      item?.entry?.url ||
      item?.entry?.slug ||
      item?.entry?.identifier
  )

  if (!first?.entry) {
    return undefined
  }

  return adaptStrapiPageRelation(first.entry)
}

function adaptStrapiGlobalVideo(
  video?: {
    id?: string
    playlist?: string | null
    playlistMobile?: string | null
    image?: StrapiGlobalImage | null
  } | null,
  videoUrlFallback?: string | null
): MCDC.Contentful.IGlobalVideo | undefined {
  const playlist = video?.playlist || videoUrlFallback

  if (!playlist) {
    return undefined
  }

  return {
    __typename: 'ContentfulGlobalVideo',
    id: video?.id || 'stage-video',
    contentful_id: video?.id || 'stage-video',
    node_locale: defaultLocale,
    playlist,
    playlistMobile: video?.playlistMobile || playlist,
    image: adaptStrapiGlobalImage(video?.image),
  }
}

function adaptStrapiModuleVideo(
  video: {
    id?: string
    titleInternal?: string | null
    youtubeId?: string | null
    headline?: string | null
    copy?: Array<StrapiCopyBlock | null> | null
    image?: StrapiGlobalImage | null
  },
  fallbackId: string,
  index: number
): MCDC.Contentful.IModuleVideo {
  const id = video.id || `${fallbackId}-video-${index}`

  return {
    __typename: 'ContentfulModuleVideo',
    id,
    contentful_id: id,
    node_locale: defaultLocale,
    titleInternal: video.titleInternal || '',
    youtubeId: video.youtubeId || '',
    headline: video.headline || '',
    copy: strapiBlocksToContentfulRichText(video.copy),
    image: adaptStrapiGlobalImage(video.image),
  }
}

function adaptStrapiVideoSliderVideos(
  videos: unknown,
  fallbackId: string
): MCDC.Contentful.IModuleVideo[] {
  if (!Array.isArray(videos)) {
    return []
  }

  return videos
    .map((video, index) => {
      if (!video || typeof video !== 'object') {
        return null
      }

      const item = video as {
        id?: string
        titleInternal?: string | null
        youtubeId?: string | null
        headline?: string | null
        videoTitel?: string | null
        copy?: Array<StrapiCopyBlock | null> | null
        image?: StrapiGlobalImage | null
        sys?: { id?: string | null } | null
      }

      if (!item.youtubeId && !item.headline && !item.videoTitel) {
        return null
      }

      return adaptStrapiModuleVideo(
        {
          ...item,
          headline: item.headline || item.videoTitel || '',
        },
        item.id || item.sys?.id || fallbackId,
        index
      )
    })
    .filter((video): video is MCDC.Contentful.IModuleVideo => video !== null)
}

function adaptStrapiStageBackground(entry: {
  background?: Array<{
    __typename?: string
    entry?: StrapiGlobalImage | {
      id?: string
      playlist?: string | null
      playlistMobile?: string | null
      image?: StrapiGlobalImage | null
    } | null
  }> | null
}): MCDC.Contentful.IGlobalImage | MCDC.Contentful.IGlobalVideo | undefined {
  for (const item of entry.background || []) {
    if (
      item.__typename === 'STRAPI__COMPONENT_LINK_GLOBAL_IMAGE' &&
      item.entry
    ) {
      const backgroundImage = adaptStrapiGlobalImage(
        item.entry as StrapiGlobalImage
      )
      if (backgroundImage) {
        return backgroundImage
      }
    }

    if (
      item.__typename === 'STRAPI__COMPONENT_LINK_GLOBAL_VIDEO' &&
      item.entry
    ) {
      const backgroundVideo = adaptStrapiGlobalVideo(
        item.entry as {
          id?: string
          playlist?: string | null
          playlistMobile?: string | null
          image?: StrapiGlobalImage | null
        }
      )
      if (backgroundVideo) {
        return backgroundVideo
      }
    }
  }

  return undefined
}

function adaptStrapiLinkToParams(
  params?: StrapiLinkToParams | null
): { internal: { content: string } } | undefined {
  if (!params) {
    return undefined
  }

  return {
    internal: {
      content: JSON.stringify(params),
    },
  }
}

function strapiMediaUrl(media?: StrapiMedia | null): string | undefined {
  if (!media) {
    return undefined
  }

  if (media.localFile?.publicURL) {
    return media.localFile.publicURL
  }

  if (!media.url) {
    return undefined
  }

  if (media.url.startsWith('http')) {
    return media.url
  }

  const apiURL = process.env.STRAPI_API_URL || process.env.GATSBY_STRAPI_API_URL || ''
  return `${apiURL.replace(/\/$/, '')}${media.url}`
}

function adaptStrapiMedia(
  media?: StrapiMedia | null
): MCDC.Contentful.IAssetImage | MCDC.Contentful.IAssetSvg | undefined {
  const publicURL = strapiMediaUrl(media)

  if (!media || !publicURL || !media.mime) {
    return undefined
  }

  return {
    __typename: 'ContentfulAsset',
    title: media.alternativeText || media.name || '',
    description: media.caption || '',
    file: {
      fileName: media.name || '',
      contentType: media.mime,
      url: publicURL,
    },
    localFile: {
      publicURL,
      childImageSharp: media.localFile?.childImageSharp,
      svg: media.localFile?.svg || undefined,
    },
  } as MCDC.Contentful.IAssetImage
}

export function adaptStrapiGlobalImage(
  image?: StrapiGlobalImage | null
): MCDC.Contentful.IGlobalImage | undefined {
  if (!image) {
    return undefined
  }

  const large = adaptStrapiMedia(image.large)
  const small = adaptStrapiMedia(image.small)

  if (!large) {
    return undefined
  }

  return {
    __typename: 'ContentfulGlobalImage',
    id: image.id,
    contentful_id: image.id,
    node_locale: defaultLocale,
    theme: image.theme || 'light',
    large: large as MCDC.Contentful.IAssetImage,
    medium: large as MCDC.Contentful.IAssetImage,
    small: (small || large) as MCDC.Contentful.IAssetImage,
  }
}

function strapiTextMarks(child: StrapiCopyChild) {
  const marks: Array<{ type: string }> = []
  if (child.bold) {
    marks.push({ type: 'bold' })
  }
  if (child.italic) {
    marks.push({ type: 'italic' })
  }
  if (child.underline) {
    marks.push({ type: 'underline' })
  }
  return marks
}

function strapiTextNode(child: StrapiCopyChild) {
  const text = child.text || ''
  const marks = strapiTextMarks(child)

  if (child.url) {
    return {
      nodeType: INLINES.HYPERLINK,
      data: { uri: child.url },
      content: [
        {
          nodeType: 'text',
          value: text,
          marks,
          data: {},
        },
      ],
    }
  }

  return {
    nodeType: 'text',
    value: text,
    marks,
    data: {},
  }
}

function strapiInlineNodes(children?: Array<StrapiCopyChild | null> | null) {
  return (children || [])
    .filter((child): child is StrapiCopyChild => !!child)
    .flatMap((child) => {
      if (child.type === 'text' && child.text) {
        return [strapiTextNode(child)]
      }

      if (child.type === 'link' && child.url) {
        return [strapiTextNode(child)]
      }

      return strapiInlineNodes(child.children)
    })
}

function strapiParagraphNode(children?: Array<StrapiCopyChild | null> | null) {
  return {
    nodeType: BLOCKS.PARAGRAPH,
    data: {},
    content: strapiInlineNodes(children),
  }
}

function strapiListItemNode(listItem: StrapiCopyChild) {
  return {
    nodeType: BLOCKS.LIST_ITEM,
    data: {},
    content: [strapiParagraphNode(listItem.children)],
  }
}

function strapiHeadingBlockType(level?: number | null) {
  switch (level) {
    case 1:
      return BLOCKS.HEADING_1
    case 2:
      return BLOCKS.HEADING_2
    case 3:
      return BLOCKS.HEADING_3
    case 4:
      return BLOCKS.HEADING_4
    case 5:
      return BLOCKS.HEADING_5
    case 6:
      return BLOCKS.HEADING_6
    default:
      return BLOCKS.HEADING_2
  }
}

function strapiBlockNode(block: StrapiCopyBlock) {
  switch (block.type) {
    case 'heading':
      return {
        nodeType: strapiHeadingBlockType(block.level),
        data: {},
        content: strapiInlineNodes(block.children),
      }
    case 'list': {
      const listType =
        block.format === 'ordered' ? BLOCKS.OL_LIST : BLOCKS.UL_LIST

      return {
        nodeType: listType,
        data: {},
        content: (block.children || [])
          .filter((child): child is StrapiCopyChild => child?.type === 'list-item')
          .map((child) => strapiListItemNode(child)),
      }
    }
    case 'paragraph':
    default:
      return strapiParagraphNode(block.children)
  }
}

export function strapiBlocksToContentfulRichText(
  blocks: unknown
): MCDC.Contentful.BasicRichText | undefined {
  if (!Array.isArray(blocks) || !blocks.length) {
    return undefined
  }

  const content = (blocks as StrapiCopyBlock[])
    .map((block) => strapiBlockNode(block))
    .filter((node) => node.content.length > 0)

  if (!content.length) {
    return undefined
  }

  const document: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content,
  }

  return {
    raw: JSON.stringify(document),
    references: [],
  }
}

type StrapiFilterOption = {
  id?: string
  title?: string | null
  titleInternal?: string | null
  identifier?: string | null
}

type StrapiFilterLevel = {
  id?: string
  titleInternal?: string | null
  hasMultipleOptions?: boolean | null
  filterOptions?: Array<StrapiFilterOption | null> | null
}

type StrapiFilterLevelOption = {
  id?: string
  titleInternal?: string | null
  filterLevel?: Array<StrapiFilterLevel | null> | null
}

function adaptStrapiFilterOption(
  option: StrapiFilterOption,
  fallbackId: string,
  index: number
) {
  const id = option.id || `${fallbackId}-option-${index}`

  return {
    id,
    contentful_id: id,
    title: option.title || '',
    titleInternal: option.titleInternal || '',
    identifier: option.identifier || '',
  }
}

function adaptStrapiFilterLevel(
  level: StrapiFilterLevel,
  fallbackId: string,
  index: number
) {
  const id = level.id || `${fallbackId}-level-${index}`

  return {
    id,
    contentful_id: id,
    titleInternal: level.titleInternal || '',
    hasMultipleOptions: level.hasMultipleOptions || false,
    filterOptions: (level.filterOptions || [])
      .map((option, optionIndex) =>
        option
          ? adaptStrapiFilterOption(option, id, optionIndex)
          : null
      )
      .filter(
        (
          option
        ): option is ReturnType<typeof adaptStrapiFilterOption> =>
          option !== null
      ),
  }
}

function adaptStrapiFilterLevelOption(
  filter?: StrapiFilterLevelOption | null,
  fallbackId = 'filter'
) {
  if (!filter?.id && !filter?.filterLevel?.length) {
    return undefined
  }

  const id = filter.id || fallbackId

  return {
    __typename: 'ContentfulModuleFilterLevelOption' as const,
    id,
    contentful_id: id,
    node_locale: defaultLocale,
    titleInternal: filter.titleInternal || '',
    filterLevel: (filter.filterLevel || [])
      .map((level, index) =>
        level ? adaptStrapiFilterLevel(level, id, index) : null
      )
      .filter(
        (
          level
        ): level is ReturnType<typeof adaptStrapiFilterLevel> =>
          level !== null
      ),
  }
}

function adaptStrapiFaqVideos(
  faqVideos: unknown,
  fallbackId: string
): MCDC.Contentful.IGlobalFaqVideo[] {
  if (!Array.isArray(faqVideos)) {
    return []
  }

  return faqVideos
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const faqVideo = item as {
        id?: string
        titleInternal?: string | null
        question?: string | null
        answer?: Array<StrapiCopyBlock | null> | null
        video?: {
          id?: string
          titleInternal?: string | null
          youtubeId?: string | null
        } | null
        image?: StrapiGlobalImage | null
      }

      if (!faqVideo.question) {
        return null
      }

      const id = faqVideo.id || `${fallbackId}-faq-video-${index}`

      return {
        __typename: 'ContentfulGlobalFaqVideo' as const,
        id,
        contentful_id: id,
        node_locale: defaultLocale,
        titleInternal: faqVideo.titleInternal || '',
        question: faqVideo.question,
        answer: strapiBlocksToContentfulRichText(faqVideo.answer),
        video: faqVideo.video?.youtubeId
          ? adaptStrapiModuleVideo(
              {
                id: faqVideo.video.id,
                titleInternal: faqVideo.video.titleInternal,
                youtubeId: faqVideo.video.youtubeId,
              },
              id,
              index
            )
          : undefined,
        image: adaptStrapiGlobalImage(faqVideo.image),
      } as MCDC.Contentful.IGlobalFaqVideo
    })
    .filter(
      (faqVideo): faqVideo is MCDC.Contentful.IGlobalFaqVideo =>
        faqVideo !== null
    )
}

function adaptStrapiModuleAmbassadors(
  ambassadors: unknown,
  fallbackId: string
): MCDC.Contentful.IModuleAmbassador[] {
  if (!Array.isArray(ambassadors)) {
    return []
  }

  return ambassadors
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const ambassador = item as {
        id?: string
        titleInternal?: string | null
        headline?: string | null
        copy?: Array<StrapiCopyBlock | null> | null
        image?: StrapiMedia | null
        urlToInstagram?: string | null
        urlToTikTok?: string | null
      }

      const id = ambassador.id || `${fallbackId}-ambassador-${index}`

      return {
        __typename: 'ContentfulModuleAmbassador' as const,
        id,
        contentful_id: id,
        node_locale: defaultLocale,
        titleInternal: ambassador.titleInternal || '',
        headline: ambassador.headline || '',
        copy: strapiBlocksToContentfulRichText(ambassador.copy),
        image: adaptStrapiMedia(ambassador.image),
        urlToInstagram: ambassador.urlToInstagram || '',
        urlToTikTok: ambassador.urlToTikTok || '',
      } as MCDC.Contentful.IModuleAmbassador
    })
    .filter(
      (ambassador): ambassador is MCDC.Contentful.IModuleAmbassador =>
        ambassador !== null
    )
}

function adaptStrapiAmbassadorFaqs(
  ambassadors: unknown,
  fallbackId: string
): MCDC.Contentful.IModuleAmbassadorFaq[] {
  if (!Array.isArray(ambassadors)) {
    return []
  }

  return ambassadors
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null
      }

      const ambassador = item as {
        id?: string
        titleInternal?: string | null
        headline?: string | null
        subline?: string | null
        image?: StrapiMedia | null
        ambassadorFilter?: StrapiFilterLevelOption | null
        faqVideo?: unknown
      }

      const id = ambassador.id || `${fallbackId}-ambassador-${index}`

      return {
        __typename: 'ContentfulModuleAmbassadorFaq' as const,
        id,
        contentful_id: id,
        node_locale: defaultLocale,
        titleInternal: ambassador.titleInternal || '',
        headline: ambassador.headline || '',
        subline: ambassador.subline || '',
        image: adaptStrapiMedia(ambassador.image),
        ambassadorFilter: adaptStrapiFilterLevelOption(
          ambassador.ambassadorFilter,
          `${id}-filter`
        ),
        faqVideo: adaptStrapiFaqVideos(ambassador.faqVideo, id),
      } as MCDC.Contentful.IModuleAmbassadorFaq
    })
    .filter(
      (
        ambassador
      ): ambassador is MCDC.Contentful.IModuleAmbassadorFaq =>
        ambassador !== null
    )
}

function moduleBase(
  moduleLink: StrapiModuleLink,
  entry: { id: string; titleInternal?: string | null },
  contentfulTypename: string
) {
  return {
    id: moduleLink.id || entry.id,
    contentful_id: entry.id,
    node_locale: defaultLocale,
    titleInternal: entry.titleInternal || '',
    __typename: contentfulTypename,
  }
}

export function mapStrapiModules(
  modules?: Array<StrapiModuleLink | null> | null
): MCDC.Contentful.UnionModules[] {
  if (!modules?.length) {
    return []
  }

  return modules
    .map((moduleLink) => {
      if (!moduleLink?.__typename || !moduleLink.entry) {
        return null
      }

      switch (moduleLink.__typename) {
        case 'STRAPI__COMPONENT_LINK_MODULE_TEASER_IMAGE': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
            image?: StrapiMedia | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleTeaserImage'),
            headline: entry.headline || '',
            copy: strapiBlocksToContentfulRichText(entry.copy),
            image: adaptStrapiMedia(entry.image),
          } as MCDC.Contentful.IModuleTeaserImage
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_TEASER_TEXT': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            linkToLabel?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleTeaserText'),
            headline: entry.headline || '',
            linkToLabel: entry.linkToLabel || '',
            copy: strapiBlocksToContentfulRichText(entry.copy),
          } as MCDC.Contentful.IModuleTeaserText
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_TEASER_JOB': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            linkToLabel?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
            image?: StrapiMedia | null
            linkToParams?: StrapiLinkToParams | null
            stickyCallToAction?: boolean | null
            linkTo?: Parameters<typeof adaptStrapiPageLinkComponent>[0]
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleTeaserJobs'),
            headline: entry.headline || '',
            linkToLabel: entry.linkToLabel || '',
            copy: strapiBlocksToContentfulRichText(entry.copy),
            image: adaptStrapiMedia(entry.image),
            linkToParams: adaptStrapiLinkToParams(entry.linkToParams),
            linkTo: adaptStrapiPageLinkComponent(entry.linkTo),
            stickyCallToAction: entry.stickyCallToAction ?? null,
          } as MCDC.Contentful.IModuleTeaserJobs
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_TEASER_MEDIA': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            theme?: string | null
            entries?: Array<{
              id: string
              layout?: string | null
              headline?: string | null
              subline?: string | null
              linkToLabel?: string | null
              copy?: Array<StrapiCopyBlock | null> | null
              image?: StrapiMedia | null
              linkTo?: Parameters<typeof adaptStrapiPageLinkComponent>[0]
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleTeaserMedia'),
            theme: entry.theme || undefined,
            entries: (entry.entries || []).map((item) => ({
              __typename: 'ContentfulGlobalMediaTeaser',
              id: item.id,
              contentful_id: item.id,
              node_locale: defaultLocale,
              layout: item.layout || '',
              headline: item.headline || '',
              subline: item.subline || '',
              linkToLabel: item.linkToLabel || '',
              copy: strapiBlocksToContentfulRichText(item.copy),
              image: adaptStrapiMedia(item.image),
              linkTo: adaptStrapiPageLinkComponent(item.linkTo),
            })),
          } as MCDC.Contentful.IModuleTeaserMedia
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_DEEP_LINK': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            entries?: Array<{
              id: string
              title?: string | null
              image?: StrapiMedia | null
              linkToParams?: StrapiLinkToParams | null
              linkTo?: Parameters<typeof adaptStrapiPageLinkComponent>[0]
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleDeepLinks'),
            headline: entry.headline || '',
            entries: (entry.entries || []).map((item) => ({
              __typename: 'ContentfulGlobalDeepLink',
              id: item.id,
              contentful_id: item.id,
              node_locale: defaultLocale,
              title: item.title || '',
              image: adaptStrapiMedia(item.image),
              linkTo: adaptStrapiPageLinkComponent(item.linkTo),
              linkToParams: adaptStrapiLinkToParams(item.linkToParams),
            })),
          } as MCDC.Contentful.IModuleDeepLinks
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_BENEFIT': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            entries?: Array<{
              id: string
              benefitDescription?: Array<StrapiCopyBlock | null> | null
              icon?: StrapiMedia | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleBenefits'),
            headline: entry.headline || '',
            entries: (entry.entries || []).map((item) => ({
              __typename: 'ContentfulGlobalBenefit',
              id: item.id,
              contentful_id: item.id,
              node_locale: defaultLocale,
              description: strapiBlocksToContentfulRichText(item.benefitDescription),
              icon: adaptStrapiMedia(item.icon),
            })),
          } as MCDC.Contentful.IModuleBenefits
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_JOB_FACT': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            entries?: Array<{
              id: string
              title?: string | null
              description?: string | null
              icon?: StrapiMedia | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleJobFacts'),
            entries: (entry.entries || []).map((fact) => ({
              __typename: 'ContentfulGlobalJobFact',
              id: fact.id,
              contentful_id: fact.id,
              node_locale: defaultLocale,
              title: fact.title || '',
              description: {
                description: fact.description || '',
              },
              icon: adaptStrapiMedia(fact.icon),
            })),
          } as MCDC.Contentful.IModuleJobFacts
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_FACT': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            facts?: string | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleFacts'),
            headline: entry.headline || '',
            facts: entry.facts ? { facts: entry.facts } : undefined,
          } as MCDC.Contentful.IModuleFacts
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_VIDEO': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            youtubeId?: string | null
            headline?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
            image?: StrapiGlobalImage | null
          }

          const video = adaptStrapiModuleVideo(entry, entry.id, 0)

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleVideo'),
            youtubeId: video.youtubeId,
            headline: video.headline,
            copy: video.copy,
            image: video.image,
          } as MCDC.Contentful.IModuleVideo
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_TESTIMONIAL': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            entries?: Array<{
              id: string
              author?: string | null
              copy?: Array<StrapiCopyBlock | null> | null
              image?: StrapiMedia | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleTestimonials'),
            entries: (entry.entries || []).map((testimonial) => ({
              __typename: 'ContentfulGlobalTestimonial',
              id: testimonial.id,
              contentful_id: testimonial.id,
              node_locale: defaultLocale,
              author: testimonial.author || '',
              copy: strapiBlocksToContentfulRichText(testimonial.copy),
              image: adaptStrapiMedia(testimonial.image),
            })),
          } as MCDC.Contentful.IModuleTestimonials
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_FAQ': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            entries?: Array<{
              id: string
              entry?: {
                id: string
                question?: string | null
                answer?: Array<StrapiCopyBlock | null> | null
              } | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleFaq'),
            headline: entry.headline || '',
            entries: (entry.entries || [])
              .map((link) => {
                const faq = link.entry
                if (!faq) {
                  return null
                }

                return {
                  __typename: 'ContentfulGlobalFaq',
                  id: faq.id,
                  contentful_id: faq.id,
                  node_locale: defaultLocale,
                  question: faq.question || '',
                  answer: strapiBlocksToContentfulRichText(faq.answer),
                  topics: [],
                }
              })
              .filter(
                (faq): faq is NonNullable<typeof faq> => faq !== null
              ),
          } as MCDC.Contentful.IModuleFaq
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_STEP': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            subline?: string | null
            titleInternal?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
            entries?: Array<{
              id: string
              title?: string | null
              description?: Array<StrapiCopyBlock | null> | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleSteps'),
            headline: entry.headline || '',
            subline: entry.subline || '',
            copy: strapiBlocksToContentfulRichText(entry.copy),
            entries: (entry.entries || []).map((step) => ({
              __typename: 'ContentfulGlobalStep',
              id: step.id,
              contentful_id: step.id,
              node_locale: defaultLocale,
              title: step.title || '',
              description: strapiBlocksToContentfulRichText(step.description),
            })),
          } as MCDC.Contentful.IModuleSteps
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_EDUCATION_TEASER': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            data?: {
              headline?: string | null
              copy?: string | null
              items?: Array<{
                headline?: string | null
                copy?: string | null
                image?: string | null
                links?: Array<{
                  label?: string | null
                  url?: string | null
                  openInNewTab?: boolean | null
                }> | null
              }> | null
            } | null
          }

          const data = entry.data

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleEducationTeaser'),
            data: {
              id: entry.id,
              headline: data?.headline || '',
              copy: data?.copy || '',
              items: (data?.items || []).map((item) => ({
                headline: item.headline || '',
                copy: item.copy || '',
                image: item.image || '',
                links: (item.links || []).map((link) => ({
                  label: link.label || '',
                  url: link.url || '',
                  openInNewTab: !!link.openInNewTab,
                })),
              })),
            },
          } as MCDC.Contentful.IModuleEducationTeaser
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_LINK_BUNDLE': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            data?: {
              headline?: string | null
              style?: string | null
              items?: Array<{
                label?: string | null
                url?: string | null
                isExternal?: boolean | null
              }> | null
            } | null
          }

          const data = entry.data

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleLinkBundle'),
            childContentfulModuleLinkBundleDataJsonNode: {
              id: entry.id,
              headline: data?.headline || '',
              style: data?.style || 'primary',
              items: (data?.items || []).map((item) => ({
                label: item.label || '',
                url: item.url || '',
                isExternal: !!item.isExternal,
              })),
            },
          } as MCDC.Contentful.IModuleLinkBundle
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_BENEFITS_LIST': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            structuredDataBenefits?: {
              headline?: string | null
              items?: Array<{
                employer?: string | null
                employee?: string | null
              }> | null
            } | null
          }

          const structuredData = entry.structuredDataBenefits

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleBenefitsList'),
            childContentfulModuleBenefitsListStructuredDataBenefitsJsonNode: {
              id: entry.id,
              headline: structuredData?.headline || '',
              items: (structuredData?.items || []).map((item) => ({
                employer: item.employer || '',
                employee: item.employee || '',
              })),
            },
          } as MCDC.Contentful.IModuleBenefitsList
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_VIDEO_SLIDER': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            headline?: string | null
            slidesPerView?: number | null
            videos?: Array<{
              id?: string
              titleInternal?: string | null
              youtubeId?: string | null
              headline?: string | null
              videoTitel?: string | null
              copy?: Array<StrapiCopyBlock | null> | null
              image?: StrapiGlobalImage | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleVideoSlider'),
            headline: entry.headline || '',
            slidesPerView: entry.slidesPerView || 3,
            videos: adaptStrapiVideoSliderVideos(entry.videos, entry.id),
          } as MCDC.Contentful.IModuleVideoSlider
        }

        case 'STRAPI__COMPONENT_LINK_AMBASSADOR_CONTAINER': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            ambassadors?: unknown
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulAmbassadorContainer'),
            ambassadors: adaptStrapiModuleAmbassadors(
              entry.ambassadors,
              entry.id
            ),
          } as MCDC.Contentful.IModuleAmbassadorContainer
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_AMBASSADOR_FAQ_MODULE': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            headline?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
            ambassadorFilter?: StrapiFilterLevelOption | null
            ambassadorData?: unknown
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleAmbassadorFaqs'),
            headline: entry.headline || '',
            copy: strapiBlocksToContentfulRichText(entry.copy),
            ambassadorFilter: adaptStrapiFilterLevelOption(
              entry.ambassadorFilter,
              `${entry.id}-filter`
            ),
            ambassadorData: adaptStrapiAmbassadorFaqs(
              entry.ambassadorData,
              entry.id
            ),
          } as MCDC.Contentful.IModuleAmbassadorFaqs
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_STAGE': {
          const entry = moduleLink.entry as {
            id: string
            headline?: string | null
            titleInternal?: string | null
            background?: Array<{
              __typename?: string
              entry?: StrapiGlobalImage | {
                id?: string
                playlist?: string | null
                playlistMobile?: string | null
                image?: StrapiGlobalImage | null
              } | null
            }> | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleStage'),
            headline: entry.headline || '',
            background: adaptStrapiStageBackground(entry),
          } as MCDC.Contentful.IModuleStage
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_RECOMMENDATION': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
          }

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleRecommendations'),
          } as MCDC.Contentful.IModuleRecommendations
        }

        case 'STRAPI__COMPONENT_LINK_MODULE_REVIEW_DETAIL': {
          const entry = moduleLink.entry as {
            id: string
            titleInternal?: string | null
            headline?: string | null
            copy?: Array<StrapiCopyBlock | null> | null
            labels?: { strapi_json_value?: string[] | null } | null
            values?: { strapi_json_value?: string[] | null } | null
          }

          const labels = entry.labels?.strapi_json_value || []
          const values = entry.values?.strapi_json_value || []

          return {
            ...moduleBase(moduleLink, entry, 'ContentfulModuleReviewDetails'),
            headline: entry.headline || '',
            copy: strapiBlocksToContentfulRichText(entry.copy),
            labels,
            values,
          } as MCDC.Contentful.IModuleReviewDetails
        }

        default:
          return null
      }
    })
    .filter((module): module is MCDC.Contentful.UnionModules => module !== null)
}
